import { LogsService } from '@services/logs';

/**
 * Parses a date string and formats it into a compact `YYYYMMDDHHmm` format,
 * ignoring the timezone information in the provided string.
 *
 * This method expects the input string to follow a format similar to
 * `"Thu Jul 17 2025 14:00:00 GMT+0200"`. The timezone and weekday are ignored
 * during parsing.
 *
 * Example:
 * ```typescript
 * parseDateStringIgnoringTimezone("Thu Jul 17 2025 14:00:00 GMT+0200");
 * // Output: "202507171400"
 * ```
 *
 * If the input date string is invalid or does not match the expected format,
 * an error is logged, and `null` is returned.
 *
 * @param dateString - The date string to parse. Must include the following:
 * - A three-letter month abbreviation (e.g., "Jul").
 * - A numeric day (e.g., "17").
 * - A four-digit year (e.g., "2025").
 * - Time in the format `HH:mm:ss`.
 *
 * @returns A string in the `YYYYMMDDHHmm` format if the input is valid.
 * Returns `null` if the input string is invalid or the parsing fails.
 */
const parseDateStringIgnoringTimezone = (dateString: string): string | null => {
  // English month abbreviations to number mapping.
  const monthMap: { [key: string]: string } = {
    Jan: '01',
    Feb: '02',
    Mar: '03',
    Apr: '04',
    May: '05',
    Jun: '06',
    Jul: '07',
    Aug: '08',
    Sep: '09',
    Oct: '10',
    Nov: '11',
    Dec: '12',
  };

  // Split the string into parts.
  // "Thu Jul 17 2025 14:00:00 GMT+0200" -> ["Thu", "Jul", "17", "2025", "14:00:00", ...]
  const parts = dateString.split(' ');

  // Destructure and check if all required parts exist.
  const [_weekday, monthStr, day, year, time] = parts;
  if (!monthStr || !day || !year || !time) {
    LogsService.error('formatLocalDateString', 'Invalid date string format:', dateString);
    return null;
  }

  const month = monthMap[monthStr];
  if (!month) {
    LogsService.error('formatLocalDateString', 'Invalid month in date string:', monthStr);
    return null;
  }

  // Extract hour and minute from the time part.
  const [hour, minute] = time.split(':');
  if (hour === undefined || minute === undefined) {
    LogsService.error('formatLocalDateString', 'Invalid time format in date string:', time);
    return null;
  }

  // Assemble the final string.
  return `${year}${month}${day.padStart(2, '0')}${hour}${minute}`;
};

/**
 * Converts a `Date` object into a localized string representation while ignoring timezone differences.
 *
 * The function processes a `Date` object by stringifying it into a standard local date-time string
 * (e.g., `"Tue Jul 15 2025 10:55:00 GMT+0200 (CEST)"`). Then, it leverages a helper function
 * (`parseDateStringIgnoringTimezone`) to return a localized string without being influenced by timezone offsets
 * within the provided `Date` object.
 *
 * This is particularly useful when consistent local date formatting is required across various time zones.
 * It ensures the local representation of a date remains unchanged regardless of the timezone of the environment
 * where the code executes.
 *
 * @param date - The `Date` object to convert into a localized string.
 *
 * @returns A localized string representation of the `Date` object, or `null` if the string could not be parsed.
 *
 * @example
 * ```typescript
 * const date = new Date("2025-07-15T08:55:00Z"); // 'Tue Jul 15 2025 10:55:00 GMT+0200 (heure d’été d’Europe centrale)'
 * const localDateString = formatDateObjectAsLocal(date);
 * console.log(localDateString);
 * // Output: "202507151055" (actual format depends on `parseDateStringIgnoringTimezone`)
 * ```
 *
 * @remarks
 * - This function relies on the helper function `parseDateStringIgnoringTimezone` to ignore timezone data
 *   embedded in the `Date` string when parsing it.
 * - The resulting string format is dependent on the implementation of the helper function used.
 *
 * @see parseDateStringIgnoringTimezone
 */
export const formatDateObjectAsLocal = (date: Date): string | null => {
  const dateString = date.toString();
  return parseDateStringIgnoringTimezone(dateString);
};
